<template>
  <Modal
    ref="modal"
    content-class=""
    full-height
    :title="title"
  >
    <div class="scroll-y q-pa-md full-height">
      <QInfiniteScroll
        class="q-col-gutter-md"
        @load="loadExcludedMentions"
      >
        <Component
          :is="factoryTypeComponent"
          v-for="mention in excludedMentions"
          :key="mention.id"
          :mention="mention"
        >
          <div class="row q-mt-sm items-center">
            <div class="col bigger">
              <template v-if="mention.comment">
                <strong>{{
                  $t("modals.excluded_mentions_modal.removed_comment")
                }}</strong>
                <span>{{ mention.comment }}</span>
              </template>
            </div>
            <div>
              <BaseButton
                v-if="!readonly"
                icon="plus"
                :icon-size="$isMobile ? 'sm' : 'xs'"
                :label="addBackButton"
                new-design
                new-icon
                @click.stop="mentionRemover(mention)"
              />
              <StreemTooltip>{{
                $t("modals.excluded_mentions_modal.add_back_tooltip")
              }}</StreemTooltip>
            </div>
          </div>
        </Component>
        <template #loading>
          <div class="row q-my-md justify-center">
            <QSpinnerDots
              color="primary"
              size="40px"
            />
          </div>
        </template>
      </QInfiniteScroll>
    </div>
  </Modal>
</template>

<script>
import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import MentionCardFactory from "shared/components/MentionCardFactory";
import SocialMentionCardFactory from "shared/components/SocialMentionCardFactory";
import { streamTypes } from "shared/constants";
import {
  getExcludedMentions,
  removeExcludedMention,
} from "shared/services/stream";

export default {
  name: "ExcludedMentionsModal",
  components: {
    Modal,
    BaseButton,
    MentionCardFactory,
    SocialMentionCardFactory,
  },
  props: {
    stream: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
    },
  },
  emits: ["updated"],
  data() {
    return {
      excludedMentions: [],
      totalPages: 0,
      loading: false,
    };
  },
  computed: {
    isSocial() {
      return this.stream.type === streamTypes.socialStream;
    },
    factoryTypeComponent() {
      return this.isSocial ? "SocialMentionCardFactory" : "MentionCardFactory";
    },
    title() {
      return this.$t("modals.excluded_mentions_modal.title", {
        streamLabel: this.stream.label,
      });
    },
    addBackButton() {
      if (this.$isMobile) {
        return "";
      }

      return this.$t("modals.excluded_mentions_modal.add_back_button");
    },
  },
  methods: {
    async loadExcludedMentions(page, done) {
      if (this.loading) return;

      try {
        this.loading = true;
        const response = await getExcludedMentions(this.stream, { page });
        this.totalPages = Number(response.headers["x-total-pages"]);

        this.excludedMentions = this.excludedMentions.concat(
          response.data.map((mention) => ({
            comment: mention.comment,
            excluded: true,
            type: mention.type,
            ...mention.target,
          }))
        );

        done(page === this.totalPages);
      } catch {
        this.$q.notify(this.$t("modals.excluded_mentions_modal.load_error"));
      } finally {
        this.loading = false;
      }
    },
    /**
     * Remove excluded mention from stream
     */
    async mentionRemover(mention) {
      this.$q.loading.show({ delay: 0 });
      await removeExcludedMention(this.stream, mention);
      this.$q.loading.hide();

      this.excludedMentions.splice(this.excludedMentions.indexOf(mention), 1);

      this.$alert(this.$t("modals.excluded_mentions_modal.add_alert"));
      this.$emit("updated");

      // close the modal if no excluded mentions remain
      if (!this.excludedMentions.length) {
        this.$refs.modal.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.comment-row {
  min-height: 26px;

  .comment {
    padding: 3px;
  }

  .title {
    font-weight: bold;
  }
}
</style>
